"use client";

import { useCallback, useRef, useState } from "react";

export interface ProfileListing {
  id: string;
  user_id?: string;
  title: string;
  description: string;
  price: string;
  currency?: string | null;
  image_urls: string[];
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: structured trio replaces
  // free-text `location` — resolved server-side (pin → main fallback).
  // `null` for orphan listings.
  shipFromAddress?: { city: string | null; state: string | null; country: string | null } | null;
  condition: string;
  grade: string;
  created_at: string;
  quantity: number;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  type?: "standard" | "preorder" | null;
  // Phase 3 (2026-06-29): accepts_offers dropped — derive via
  // isOfferEnabledFormat(listing_format) from @kraahl/contract.
  offers_pending_count?: number | null;
}

interface UseProfileListingsOptions {
  profileId: string;
  initialListings: ProfileListing[];
  initialHasMore: boolean;
  pageSize?: number;
}

interface UseProfileListingsResult {
  listings: ProfileListing[];
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 24;

export function useProfileListings({
  profileId,
  initialListings,
  initialHasMore,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseProfileListingsOptions): UseProfileListingsResult {
  const [listings, setListings] = useState<ProfileListing[]>(initialListings);
  const [hasMore, setHasMore] = useState<boolean>(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard concurrent triggers (IntersectionObserver can fire several times before
  // state from the in-flight fetch lands). A ref is the only state that
  // updates synchronously inside the same tick.
  const inFlightRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (inFlightRef.current || isLoading || !hasMore) return;
    inFlightRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const url = new URL("/api/listings/by-user", window.location.origin);
      url.searchParams.set("user_id", profileId);
      url.searchParams.set("offset", String(listings.length));
      url.searchParams.set("limit", String(pageSize));
      const res = await fetch(url.toString(), { credentials: "same-origin" });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = (await res.json()) as {
        listings: ProfileListing[];
        hasMore: boolean;
      };
      setListings((prev) => [...prev, ...(json.listings ?? [])]);
      setHasMore(Boolean(json.hasMore));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [profileId, listings.length, pageSize, hasMore, isLoading]);

  return { listings, hasMore, isLoading, error, loadMore };
}
